import { useTimeZone } from '@/auth/useTimeZone.js';
import { FormModal, SubmitProps } from '@/components/form-modal.js';
import { FormTextArea } from '@/components/forms/form-text-area.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import {
  emptyQueryOptions,
  getCoachClasses,
  getMeCoach,
  invalidateCoachPortalSessionsQueries,
  requestSubstitutionAsCoach,
} from '@/lib/api-calls.js';
import {
  formatSubRequestUsageCount,
  getSubRequestAttendanceImpact,
  type SubRequestAttendanceImpact,
} from '@/lib/sub-request-attendance-impact.js';
import { cn } from '@/lib/tailwind-utils.js';
import { formatSubRequestDateTime, getDesiredAutoOriginalTeacherIssue } from '@airreading/common';
import {
  OriginalTeacherIssue,
  requestSubstituteConfirmDialogSchema,
  SUBSTITUTION_REQUEST_REASON_MAX_LENGTH,
} from '@airreading/common/data';
import { useFeatureFlag } from '@airreading/feature-flags/web';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useMemo } from 'react';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

type RequestSubstituteConfirmDialogForm = z.infer<ReturnType<typeof requestSubstituteConfirmDialogSchema>>;

interface RequestSubstituteConfirmDialogProps {
  sessionId: string;
  studentGroupId: string;
  startInstant: Temporal.Instant;
  endInstant: Temporal.Instant;
  trigger: ReactNode;
  disabled?: boolean;
}

function AttendanceImpactMessage({ impact }: { impact: SubRequestAttendanceImpact }) {
  return (
    <div className="text-sm pt-2" data-testid="request-substitute-dialog-attendance-impact-message">
      {impact.messageParts.map(({ text, isBold }, index) =>
        isBold ? (
          <span key={`attendance-impact-message-${index}`} className="font-semibold text-foreground">
            {text}
          </span>
        ) : (
          <span key={`attendance-impact-message-${index}`}>{text}</span>
        ),
      )}
    </div>
  );
}

function AttendancePolicyUsageSection({ impact }: { impact: SubRequestAttendanceImpact }) {
  const { totalUsed, totalAllowance, shortNoticeUsed, shortNoticeAllowance } = impact;

  return (
    <div className="space-y-2" data-testid="request-substitute-dialog-attendance-impact">
      <div className="flex items-center justify-between gap-4 text-sm">
        <span>Sub requests used</span>
        <span data-testid="request-substitute-dialog-sub-requests-used">
          {formatSubRequestUsageCount(totalUsed, totalAllowance)}
        </span>
      </div>
      <div className="flex items-center justify-between gap-4 text-sm">
        <span>Short-notice requests used</span>
        <span data-testid="request-substitute-dialog-short-notice-used">
          {formatSubRequestUsageCount(shortNoticeUsed, shortNoticeAllowance)}
        </span>
      </div>
      <AttendanceImpactMessage impact={impact} />
    </div>
  );
}

export function RequestSubstituteConfirmDialog({
  sessionId,
  studentGroupId,
  startInstant,
  endInstant,
  trigger,
  disabled,
}: RequestSubstituteConfirmDialogProps) {
  const { toast } = useToast();
  const { timeZone } = useTimeZone();
  const queryClient = useQueryClient();
  const attendancePolicyV2Enabled = useFeatureFlag('teacher-attendance-policy-v2', false);
  const formSchema = useMemo(
    () => requestSubstituteConfirmDialogSchema(attendancePolicyV2Enabled),
    [attendancePolicyV2Enabled],
  );
  const { mutateAsync } = useMutation({
    mutationFn: requestSubstitutionAsCoach,
  });

  const { data: coachProfile } = useQuery({
    ...getMeCoach(),
    enabled: attendancePolicyV2Enabled && !disabled,
  });
  const coachId = coachProfile?.id;
  const { data: coachClasses } = useQuery({
    ...(coachId ? getCoachClasses(coachId, false) : emptyQueryOptions),
    enabled: attendancePolicyV2Enabled && !disabled && !!coachId,
  });

  const attendanceImpact = useMemo(() => {
    if (!attendancePolicyV2Enabled) {
      return null;
    }

    const studentGroup = coachClasses?.find((group) => group.id === studentGroupId);
    const allowance = studentGroup?.attendancePolicyV2;

    if (!allowance) {
      return null;
    }

    const isShortNotice =
      getDesiredAutoOriginalTeacherIssue({
        sessionStartInstant: startInstant,
        originalTeacherRequestedByInstant: Temporal.Now.instant(),
      }) === OriginalTeacherIssue.short_notice_sub_request;

    return getSubRequestAttendanceImpact({
      allowance,
      isShortNotice,
    });
  }, [attendancePolicyV2Enabled, coachClasses, studentGroupId, startInstant]);

  const { sessionDateFormatted, sessionTimeFormatted } = useMemo(
    () => formatSubRequestDateTime(startInstant, endInstant, timeZone),
    [startInstant, endInstant, timeZone],
  );

  const sessionDateTime = `${sessionDateFormatted}, ${sessionTimeFormatted}`;

  const onSubmit = async ({ formData, closeModal, reset }: SubmitProps<RequestSubstituteConfirmDialogForm>) => {
    try {
      const trimmedRequestReason = formData.requestReason.trim();
      await mutateAsync({
        sessionId,
        requestReason: trimmedRequestReason || undefined,
      });

      toast({
        title: `Your sub request has been submitted. The session on ${sessionDateFormatted}, ${sessionTimeFormatted} has been removed from your calendar.`,
      });
      closeModal();
      reset();
      invalidateCoachPortalSessionsQueries(queryClient);

      if (coachId) {
        void queryClient.invalidateQueries(getCoachClasses(coachId, false));
      }
    } catch (e) {
      console.log(e);
    }
  };

  if (disabled) {
    return <>{trigger}</>;
  }

  return (
    <FormModal
      modalTitle="Request a substitute"
      onSubmit={onSubmit}
      formSchema={formSchema}
      trigger={trigger}
      confirmBtnLabel="Request"
      defaultValues={{
        requestReason: '',
      }}
      dialogContentProps={{ className: 'lg:max-w-lg' }}
      dataTestId="session-modal-request-substitute"
      validateOnOpen
    >
      {({ form }) => {
        const requestReasonLength = form.watch('requestReason').length;
        const isOverRequestReasonLimit = requestReasonLength > SUBSTITUTION_REQUEST_REASON_MAX_LENGTH;

        return (
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground" data-testid="request-substitute-dialog-session-copy">
              {attendancePolicyV2Enabled
                ? `You are requesting a substitute for ${sessionDateTime}. This session will be removed from your calendar.`
                : `Submit a substitute request for ${sessionDateTime}? This will remove it from your calendar.`}
            </p>
            {attendanceImpact ? <AttendancePolicyUsageSection impact={attendanceImpact} /> : null}
            <FormTextArea
              form={form}
              name="requestReason"
              label="Reason"
              labelEndContent={
                <span className={cn('text-sm text-muted-foreground', isOverRequestReasonLimit && 'text-destructive')}>
                  {requestReasonLength} / {SUBSTITUTION_REQUEST_REASON_MAX_LENGTH}
                </span>
              }
              textAreaProps={{
                required: attendancePolicyV2Enabled,
                placeholder: attendancePolicyV2Enabled
                  ? 'Add a reason for your request'
                  : 'Add a reason for your request (optional)',
                'data-testid': 'session-modal-request-substitute-input-reason',
              }}
            />
          </div>
        );
      }}
    </FormModal>
  );
}
